import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import ContactCTA from "@/components/ContactCTA";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { PROJECTS } from "@/lib/projects";

const ease = [0.22, 1, 0.36, 1];
const MotionLink = motion(Link);

// All unique industry tags
const FILTERS = [
    "All",
    "Branding",
    "Websites",
    "Performance",
    "Content",
    "Commerce",
];

export default function Work() {
    const [filter, setFilter] = useState("All");

    const filtered =
        filter === "All"
            ? PROJECTS
            : PROJECTS.filter((p) => p.tags.includes(filter));

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <>
            <Seo {...SEO.work} />
            <PageHero
                data-testid="work-page-hero"
                kicker="/ Selected work · 02"
                meta="2024 — 2025"
                title="Brands we've"
                accent="grown"
                tagline="Seven brands, seven stories. Each one grown from where it stood — with strategy, craft, and a small team that stayed."
            />

            {/* Filters */}
            <section
                data-testid="work-filters"
                className="border-t border-b border-black/10 py-6"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-wrap items-center gap-3">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/50 mr-2">
                        Filter
                    </span>
                    {FILTERS.map((f) => {
                        const active = filter === f;
                        return (
                            <button
                                key={f}
                                data-testid={`work-filter-${f.toLowerCase()}`}
                                onClick={() => setFilter(f)}
                                className={`text-xs uppercase tracking-[0.16em] font-bold px-3.5 py-2 border transition-all duration-300 ${
                                    active
                                        ? "bg-obsidian text-bone border-obsidian"
                                        : "bg-transparent text-black/70 border-black/20 hover:border-black hover:text-black"
                                }`}
                            >
                                {f}
                            </button>
                        );
                    })}
                    <span className="ml-auto text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                        Showing {filtered.length} of {PROJECTS.length}
                    </span>
                </div>
            </section>

            {/* Featured */}
            {featured && (
                <section
                    data-testid="work-featured"
                    className="relative py-20 lg:py-24"
                >
                    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55 mb-6">
                            (Featured)
                        </div>
                        <motion.article
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease }}
                            data-testid={`work-featured-${featured.id}`}
                            className="group grid grid-cols-12 gap-y-8 lg:gap-x-10 items-start"
                        >
                            <Link
                                to={`/work/${featured.id}`}
                                data-testid={`work-featured-link-${featured.id}`}
                                className="col-span-12 lg:col-span-8 relative overflow-hidden aspect-[16/10] bg-black/5 block"
                            >
                                <img
                                    src={featured.img}
                                    alt={featured.title}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                                <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference">
                                    <span>{featured.industry}</span>
                                    <span>{featured.year}</span>
                                </div>
                                <div className="absolute bottom-4 right-4 text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    View case study →
                                </div>
                            </Link>
                            <div className="col-span-12 lg:col-span-4 lg:pt-6">
                                {featured.logo && (
                                    <img
                                        src={featured.logo}
                                        alt=""
                                        aria-hidden
                                        className="h-8 w-auto object-contain mb-4"
                                    />
                                )}
                                <Link to={`/work/${featured.id}`}>
                                    <h2 className="font-display font-black uppercase tracking-tight text-4xl lg:text-5xl leading-[0.95] hover:text-olive transition-colors duration-300">
                                        {featured.title}
                                    </h2>
                                </Link>
                                <p className="mt-4 text-black/70 leading-relaxed">
                                    {featured.blurb}
                                </p>

                                <div className="mt-8 pt-8 border-t border-black/15 flex flex-wrap items-center gap-6">
                                    <div>
                                        <div className="font-display font-black text-3xl text-[#2E5019]">
                                            {featured.result}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/55 mt-1">
                                            in {featured.window}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {featured.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] uppercase tracking-[0.16em] font-bold border border-black/20 px-2.5 py-1.5"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.article>
                    </div>
                </section>
            )}

            {/* Grid */}
            <section
                data-testid="work-grid"
                className="relative py-16 lg:py-20"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                        {rest.map((p, i) => (
                            <MotionLink
                                key={p.id}
                                to={`/work/${p.id}`}
                                data-testid={`work-card-${p.id}`}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.8,
                                    ease,
                                    delay: (i % 6) * 0.06,
                                }}
                                className="group block"
                            >
                                <div className="relative overflow-hidden aspect-[4/3] bg-black/[0.04]">
                                    <img
                                        src={p.img}
                                        alt={p.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-[1.04]"
                                        loading="lazy"
                                    />
                                    <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-3">
                                        {p.logo ? (
                                            <span className="inline-flex items-center bg-bone/95 backdrop-blur-sm px-2 py-1 h-8">
                                                <img
                                                    src={p.logo}
                                                    alt=""
                                                    aria-hidden
                                                    className="h-4 lg:h-5 w-auto object-contain"
                                                />
                                            </span>
                                        ) : (
                                            <span className="text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference">
                                                {p.title}
                                            </span>
                                        )}
                                        <span className="text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference">
                                            {p.year}
                                        </span>
                                    </div>
                                    <div className="absolute bottom-4 right-4 text-bone text-[10px] uppercase tracking-[0.18em] font-bold mix-blend-difference opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        View case study →
                                    </div>
                                </div>

                                <div className="pt-5">
                                    <div className="flex items-baseline justify-between gap-4">
                                        <h3 className="font-display font-bold tracking-tight text-2xl group-hover:text-olive transition-colors duration-300">
                                            {p.title}
                                        </h3>
                                        <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#2E5019] shrink-0">
                                            {p.result}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">
                                        {p.industry}
                                    </div>
                                    <p className="mt-4 text-sm text-black/70 leading-relaxed">
                                        {p.blurb}
                                    </p>
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {p.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] uppercase tracking-[0.16em] font-bold border border-black/20 px-2.5 py-1.5"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </MotionLink>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <div
                            data-testid="work-empty"
                            className="py-24 text-center font-serif italic text-2xl text-black/50"
                        >
                            No projects tagged with &ldquo;{filter}&rdquo; yet.
                        </div>
                    )}
                </div>
            </section>

            <ContactCTA
                kicker="See a story you recognise?"
                title="Let's write yours."
                body="If your brand feels ready to grow — or stuck in a plateau — we'd love to hear where you are."
            />
        </>
    );
}
